import { useEffect, useMemo } from 'react'
import { useWatch } from 'react-hook-form'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import { MediaType } from '~/common/schemas/media'
import { MAX_SKU_IMAGES } from '~/constants'
import MediaShowcase from '~/products/components/media-showcase'
import { useDimensionHelpers } from '~/products/hooks/useDimensionHelpers'
import {
    type ProductFormSchema,
    type StockUnitFormSchema,
    useProductFormContext,
} from '~/products/hooks/useProductForm'
import { getMediaIDSet, getMediaNameSet } from '~/products/utils/updateChannelProductMedia'

function areImagesReady(images: Array<{ status?: string | null }>) {
    if (!images.length) {
        return false
    }

    return images.every((image) => image.status !== 'ready')
}

type Props = {
    index: number
    stockUnit: StockUnitFormSchema
}

export default function ProductStockUnitImages({ index, stockUnit }: Props) {
    const imageName = `stock_units.${index}.images` as const
    const { setValue, getValues, control } = useProductFormContext()
    const { getDimensionOptionName } = useDimensionHelpers()
    const images = useWatch({ control, name: imageName, exact: true })

    const options = useMemo(() => {
        return (
            stockUnit.dimension_options?.map((option) =>
                getDimensionOptionName(option.product_dimension.index, option.id)
            ) ?? []
        )
    }, [getDimensionOptionName, stockUnit?.dimension_options])

    /**
     * Update stock unit id for each images when images change and stock unit id is available
     * Update channel stock unit images for all channel product when stock unit images change
     */
    useEffect(() => {
        if (!areImagesReady(images)) {
            return
        }

        const channelProducts = getValues('channel_products')
        const id = getValues(`stock_units.${index}.id`)
        if (typeof id === 'number') {
            images.forEach((_, index) => {
                setValue(`${imageName}.${index}.stock_unit`, id)
            })
        }

        setValue(
            'channel_products',
            updateChannelStockUnitImages(channelProducts, images, index, id)
        )
    }, [images, imageName, index, getValues, setValue])

    return (
        <Box mb={1}>
            <Box mt={1}>
                <Typography component="div" variant="body2">
                    {stockUnit.sku ? (
                        <>
                            <Typography variant="subtitle2" sx={{ display: 'inline-block' }}>
                                SKU
                            </Typography>
                            : {stockUnit.sku}
                        </>
                    ) : (
                        interpolate(gettext('Stock unit %s'), [index + 1])
                    )}

                    {options.length > 0 && (
                        <span style={{ marginLeft: 4 }}>({options.join(', ')})</span>
                    )}
                </Typography>
            </Box>
            <MediaShowcase
                mediaPath="grouped_media"
                fieldPath={imageName}
                mediaType={MediaType.IMAGE}
                maxMediaObjs={MAX_SKU_IMAGES}
            />
        </Box>
    )
}

function updateChannelStockUnitImages(
    channelProducts: ProductFormSchema['channel_products'],
    stockUnitImages: StockUnitFormSchema['images'],
    stockUnitIndex: number,
    stockUnitId: string | number
) {
    const imageIds = getMediaIDSet(stockUnitImages)
    const imageNames = getMediaNameSet(stockUnitImages)

    // TODO: Improve this, currently very efficient
    return channelProducts.map((cp) => {
        /**
         * if channel product has specific images, return as is
         * These images are handled in the channel product edit component
         */
        if (cp.has_channel_specific_media) {
            return cp
        }

        const updatedChannelStockUnits = cp.channel_stock_units.map((csu, index) => {
            if (index !== stockUnitIndex) {
                return csu
            }

            // Remove deleted images
            const images = csu.images.filter((image) => {
                return image.stock_unit_image?.id
                    ? imageIds.has(image.stock_unit_image.id)
                    : imageNames.has(image.stock_unit_image?.name ?? '')
            })

            // Update or add images
            stockUnitImages.forEach((stockUnitImage) => {
                let image

                if (stockUnitImage.id) {
                    image = images.find(
                        (channelStockUnitImage) =>
                            channelStockUnitImage.stock_unit_image?.id === stockUnitImage.id
                    )
                } else {
                    image = images.find(
                        (channelStockUnitImage) =>
                            channelStockUnitImage.stock_unit_image?.name === stockUnitImage.name
                    )
                }

                if (image) {
                    image.display_order = stockUnitImage.display_order
                } else {
                    const channelStockUnitImage = {
                        stock_unit_image: stockUnitImage,
                        display_order: stockUnitImage.display_order,
                        stock_unit: typeof stockUnitId === 'number' ? stockUnitId : undefined,
                    }

                    images.push(channelStockUnitImage)
                }
            })

            return { ...csu, images: [...images].sort((a, b) => a.display_order - b.display_order) }
        })

        return { ...cp, channel_stock_units: updatedChannelStockUnits }
    })
}
